import { useState, useEffect } from "react";
import { X } from "lucide-react";

const currentYear = new Date().getFullYear();

export default function VehicleModal({ open, onClose, onSave, vehicle, saving }) {
  const [form, setForm] = useState({
    make: "",
    model: "",
    year: "",
    fuel_type: "gas",
  });

  useEffect(() => {
    if (vehicle) {
      setForm({
        make: vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year ? String(vehicle.year) : "",
        fuel_type: vehicle.fuel_type || "gas",
      });
    } else {
      setForm({ make: "", model: "", year: "", fuel_type: "gas" });
    }
  }, [vehicle, open]);

  if (!open) return null;

  const valid =
    form.make.trim() &&
    form.model.trim() &&
    form.year &&
    parseInt(form.year) >= 1980 &&
    parseInt(form.year) <= currentYear + 1;

  const handleSave = () => {
    if (!valid) return;
    onSave({
      make: form.make.trim(),
      model: form.model.trim(),
      year: parseInt(form.year),
      fuel_type: form.fuel_type,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#121214] border border-white/10 w-full max-w-md sm:rounded-lg overflow-hidden rounded-t-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-lg font-bold text-white">
            {vehicle ? "Edit Vehicle" : "Add Your Vehicle"}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">MAKE *</label>
            <input
              value={form.make}
              onChange={(e) => setForm({ ...form, make: e.target.value })}
              className="input-base"
              placeholder="Toyota"
              data-testid="vehicle-make-input"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">MODEL *</label>
            <input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="input-base"
              placeholder="Camry"
              data-testid="vehicle-model-input"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">YEAR *</label>
            <input
              type="number"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              className="input-base"
              placeholder="2022"
              min={1980}
              max={currentYear + 1}
              data-testid="vehicle-year-input"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">FUEL TYPE *</label>
            <div className="flex gap-2" data-testid="vehicle-fuel-toggle">
              {["gas", "hybrid", "electric"].map((ft) => (
                <button
                  key={ft}
                  type="button"
                  onClick={() => setForm({ ...form, fuel_type: ft })}
                  className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors ${
                    form.fuel_type === ft
                      ? "border-white text-white bg-white/5"
                      : "border-white/10 text-zinc-500 hover:text-zinc-300"
                  }`}
                  data-testid={`fuel-type-${ft}`}
                >
                  {ft === "gas" ? "Gas" : ft === "hybrid" ? "Hybrid" : "Electric"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-white/10 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!valid || saving}
            className="flex-1 py-2.5 bg-[#FF3B30]/20 border border-[#FF3B30]/30 text-xs font-bold uppercase tracking-widest text-[#FF3B30] hover:bg-[#FF3B30]/30 disabled:opacity-50 transition-colors"
            data-testid="vehicle-save-btn"
          >
            {saving ? "Saving..." : "Save Vehicle"}
          </button>
        </div>
      </div>
    </div>
  );
}
